/**
 * @file planUtils.js
 * @description Utility functions for plan management, including feature listing and price formatting.
 */

/**
 * Generates a list of translated features for a given plan.
 * @param {Object} plan - The plan object.
 * @param {Function} t - The translation function from i18next.
 * @returns {Array<string>} An array of translated feature strings.
 */
/**
 * Picks the label a feature definition carries for the active language.
 * @param {Object} def - The feature definition row from the API.
 * @param {string} language - The active i18next language (e.g. 'es', 'en-US').
 * @returns {string} The label in the matching language.
 */
export const getFeatureLabel = (def, language) => (
    String(language || '').split('-')[0] === 'en' ? def.label_en : def.label_es
);

// `feature_definitions.icon_ref` stores Lucide-style names, not Bootstrap Icons classes, so it
// cannot be interpolated into `bi bi-${icon_ref}` directly — that yields no icon at all.
const FEATURE_ICON_CLASSES = {
    Receipt: 'bi-receipt',
    BarChart: 'bi-file-earmark-bar-graph',
    CreditCard: 'bi-credit-card',
    GraphUp: 'bi-graph-up-arrow',
    Shield: 'bi-shield-lock',
    Headphones: 'bi-headset',
    Truck: 'bi-truck',
    DollarSign: 'bi-cash-coin',
    Palette: 'bi-palette',
    TrendingUp: 'bi-graph-up',
    Bell: 'bi-bell',
    FileText: 'bi-file-earmark-text',
    RefreshCw: 'bi-arrow-repeat',
    Wallet: 'bi-wallet2',
    Sparkles: 'bi-stars',
};

/**
 * Maps a feature definition's `icon_ref` to its Bootstrap Icons class.
 * @param {string} iconRef - The `icon_ref` value from the API.
 * @returns {string} A `bi-*` class, falling back to a generic check icon.
 */
export const getIconClass = (iconRef) => FEATURE_ICON_CLASSES[iconRef] || 'bi-check-circle';

/**
 * Generates a list of translated features for a given plan.
 * @param {Object} plan - The plan object.
 * @param {Function} t - The translation function from i18next.
 * @param {Array} featureDefinitions - The dynamic feature definitions from the API.
 * @param {string} language - The active i18next language, for the feature definition labels.
 * @returns {Array<string>} An array of translated feature strings.
 */
export const getPlanFeaturesList = (plan, t, featureDefinitions = [], language = '') => {
    const features = [];

    // --- Core Limits (Hardcoded as they are fundamental structure) ---
    // Businesses
    if (plan.max_businesses === -1) {
        features.push(t('planManagement.unlimited') + ' ' + t('planManagement.businesses'));
    } else {
        features.push(`${plan.max_businesses} ${plan.max_businesses === 1 ? t('planManagement.featureNames.businessSingular') : t('planManagement.businesses')}`);
    }

    // Users
    if (plan.max_users === -1) {
        features.push(t('planManagement.unlimited') + ' ' + t('planManagement.usersPerBusiness'));
    } else {
        features.push(`${plan.max_users} ${plan.max_users === 1 ? t('planManagement.featureNames.userSingular') : t('planManagement.usersPerBusiness')}`);
    }

    // Products
    if (plan.max_products === -1) {
        features.push(t('planManagement.unlimited') + ' ' + t('planManagement.products'));
    } else {
        features.push(`${plan.max_products} ${t('planManagement.products')}`);
    }

    // Sales Limit
    if (plan.monthly_sales_limit === null || plan.monthly_sales_limit >= 50000) {
        features.push(t('planManagement.unlimited') + ' ' + t('planManagement.featureNames.sales') + ' ' + t('planManagement.perMonth'));
    } else {
        features.push(`${plan.monthly_sales_limit} ${t('planManagement.featureNames.sales')} ${t('planManagement.perMonth')}`);
    }

    // --- Dynamic Features ---
    if (plan.features && featureDefinitions && featureDefinitions.length > 0) {
        // Map features based on display_order
        featureDefinitions.forEach(def => {
            const val = plan.features[def.feature_key];

            // Toggle check: if val is falsy/undefined, skip (except potentially for complex types handled inside)
            // For complex types like installments, we check safely inside.

            if (def.type === 'complex') {
                if (def.feature_key === 'max_installments') {
                    // Original Installments Logic
                    if (plan.features.max_installments) {
                        const maxInstallments = plan.features.max_installments;

                        if (maxInstallments === 1) {
                            features.push(t('planManagement.featureNames.cashOnlySales'));
                        } else {
                            const maxSalesWithInstallments = plan.features.max_sales_with_installments;
                            const totalSalesLimit = plan.monthly_sales_limit;

                            let salesPart = '';
                            if (maxSalesWithInstallments && maxSalesWithInstallments !== -1 && maxSalesWithInstallments !== totalSalesLimit) {
                                salesPart = t('planManagement.featureNames.monthlySales', { count: maxSalesWithInstallments });
                            } else {
                                salesPart = t('planManagement.featureNames.allSales');
                            }

                            let installmentsPart = '';
                            if (maxInstallments === -1) {
                                installmentsPart = t('planManagement.featureNames.unlimitedInstallments');
                            } else {
                                installmentsPart = t('planManagement.featureNames.upToInstallments', { count: maxInstallments });
                            }

                            features.push(t('planManagement.featureNames.salesWithInstallmentsCombined', { salesPart, installmentsPart }));
                        }
                    }
                }
                return;
            }

            // Standard types (text/boolean)
            if (!val) return; // Skip if feature not enabled/present

            if (def.type === 'text') {
                if (def.feature_key === 'reports') {
                    if (val !== 'none') {
                        features.push(t(`planManagement.reportLevels.${val}`));
                    }
                } else if (def.feature_key === 'roles') {
                    const rolesLevel = typeof val === 'string' ? val : 'basic';
                    features.push(t(`planManagement.featureNames.roles_${rolesLevel}`, { defaultValue: t('planManagement.featureNames.roles') }));
                } else if (def.feature_key === 'receipts') {
                    const receiptKey = typeof val === 'string' ? val : 'basic';
                    features.push(t(`planManagement.receiptLevels.${receiptKey}`, { defaultValue: t('planManagement.featureNames.receipts') }));
                } else {
                    // Fallback for generic text features: "Label: Value"
                    features.push(`${getFeatureLabel(def, language)}: ${val}`);
                }
            } else if (def.type === 'boolean') {
                // For boolean, if true, just show the label
                features.push(getFeatureLabel(def, language));
            }
        });
    }

    return features;
};

/**
 * Formats a price according to the currency and locale.
 * @param {number} price - The price amount.
 * @param {string} currency - The currency code (e.g., 'COP').
 * @returns {string} The formatted price string.
 */
export const formatPrice = (price, currency) => {
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: currency || 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(price);
};
